import { useCallback, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import backend from '../../services/backend';
import { Card } from '../../components/Card';
import { HelpTip } from '../../components/HelpTip';
import { CategoryNeighborhoodScatter } from './CategoryNeighborhoodScatter';
import { CategoryNeighborhoodWorklist } from './CategoryNeighborhoodWorklist';

export function CategoryNeighborhoodCard({
  categoryId,
  onSelectCluster,
}: {
  categoryId: string;
  onSelectCluster: (clusterId: number) => void;
}) {
  const [hoveredId, setHoveredId] = useState<number | null>(null);
  const { data, isLoading, isError } = useQuery({
    queryKey: ['categoryNeighborhood', categoryId],
    queryFn: () => backend.getCategoryNeighborhood(categoryId),
    enabled: !!categoryId,
  });

  const goToCategory = useCallback(
    (id: number) => window.open(`/categories/${id}`, '_blank', 'noopener'),
    [],
  );

  const points = isError ? [] : (data?.points ?? []);
  const anchors = isError ? [] : (data?.anchors ?? []);
  const summary = isLoading
    ? 'Loading…'
    : isError
      ? 'Couldn’t load'
      : `${data?.candidateCount ?? 0} candidates · ${anchors.length} neighbors`;

  return (
    <Card
      header={
        <div className="flex items-baseline justify-between">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-zinc-100">
            Category neighborhood{' '}
            <HelpTip text="Plots this category's page clusters next to the nearest other categories in embedding space, to surface clusters that sit closer to a foreign category than their own — likely mislabels." />
          </h2>
          <span className="text-xs text-gray-500 dark:text-zinc-400">{summary}</span>
        </div>
      }
    >
      {isLoading ? (
        <div className="h-80 animate-pulse rounded-lg bg-gray-100 dark:bg-zinc-700/40" />
      ) : isError ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Couldn’t load the neighborhood.</p>
      ) : points.length === 0 ? (
        <p className="text-sm text-gray-500 dark:text-zinc-400">No clusters to display.</p>
      ) : (
        <>
          <div className="flex flex-col gap-4 lg:flex-row">
            <div className="lg:w-2/3">
              <CategoryNeighborhoodScatter
                points={points}
                anchors={anchors}
                hoveredId={hoveredId}
                onHover={setHoveredId}
                onSelectCluster={onSelectCluster}
                onSelectCategory={goToCategory}
              />
            </div>
            <div className="lg:w-1/3">
              <CategoryNeighborhoodWorklist
                points={points}
                hoveredId={hoveredId}
                onHover={setHoveredId}
                onSelectCategory={goToCategory}
              />
            </div>
          </div>
          <NeighborhoodLegend />
        </>
      )}
    </Card>
  );
}

function NeighborhoodLegend() {
  return (
    <div className="mt-3 border-t border-gray-200 pt-2 text-xs text-gray-500 dark:border-zinc-700 dark:text-zinc-400">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
        <LegendMark className="rounded-full bg-blue-500" label="own cluster (size = pages)" />
        <LegendMark
          className="rotate-45 rounded-xs bg-gray-400 dark:bg-zinc-500"
          label="neighbor category (one color each)"
        />
        <LegendMark
          className="rounded-full border-2 border-red-500 dark:border-red-400"
          label="misplaced — closer to a neighbor than to this category"
        />
        <LegendMark
          className="rounded-full border-2 border-amber-500 dark:border-amber-400"
          label="borderline"
        />
      </div>
      <p className="mt-1.5 leading-snug">
        Positions are a 2D projection of embedding space — closer means more similar; the axes themselves have no
        meaning. Hover to link the chart with the candidate list; click a cluster to jump to it below, or a
        neighbor / list row to open that category. Candidates are ranked by margin (how much closer the cluster
        sits to the neighbor than to its own category) — higher is worse.
      </p>
    </div>
  );
}

function LegendMark({ className, label }: { className: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={clsx('inline-block size-2.5 shrink-0', className)} />
      {label}
    </span>
  );
}
